import {
    AssertionDescriptors,
    CalculatedAgeAssertionDescriptorValues,
    CaseStatusTypesEnum,
    CaseSubStatusTypesEnum,
} from './Constants';
import {
    IAssertionDescriptorModel,
    IAssertionDescriptorValueModel,
} from '../interfaces/IAssertionDescriptor';
import { ICaseAssertionModel, ICaseModel, ICaseStatusModel } from '../interfaces/ICase';

import Common from '../stores/Common';

export default class CaseDataCustomLogicHelper {
    static Run(currentCase: ICaseModel, assertionDescriptors: IAssertionDescriptorModel[]) {
        if (!currentCase.caseAssertions) currentCase.caseAssertions = [];
        CaseDataCustomLogicHelper.RunAgeRelatedCustomLogic(currentCase, assertionDescriptors);
        CaseDataCustomLogicHelper.RunSmokingRelatedCustomLogic(currentCase, assertionDescriptors);
        CaseDataCustomLogicHelper.RunDemandRelatedCustomLogic(currentCase, assertionDescriptors);
    }

    private static RunAgeRelatedCustomLogic(
        currentCase: ICaseModel,
        assertionDescriptors: IAssertionDescriptorModel[]
    ) {
        //#region FIND ALL THE CASE ASSERTION MATCHES FROM THE CURRENT CASE
        const livingDeceasedCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.LivingDeceased.Guid.toUpperCase()
        );
        const dobCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.DOB.Guid.toUpperCase()
        );
        const dateOfDeathCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.DateOfDeath.Guid.toUpperCase()
        );
        const calculatedAgeRangeCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.CalculatedAgeRange.Guid.toUpperCase()
        );
        const calculatedExactAgeCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.CalculatedExactAge.Guid.toUpperCase()
        );
        const estimatedAgeCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.EstimatedAge.Guid.toUpperCase()
        );
        const resolutionAgreementDateCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.ResolutionAgreementDate.Guid.toUpperCase()
        );
        const dismissalDateCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.DismissalDate.Guid.toUpperCase()
        );

        //#endregion

        //#region  RESET ALL INFERRED VALUES TO NULL; THE LOGIC BELOW CAN RECALCULATE
        if (calculatedAgeRangeCaseAssertionMatch)
            calculatedAgeRangeCaseAssertionMatch.assertionDescriptorValueGuid = undefined;

        if (calculatedExactAgeCaseAssertionMatch)
            calculatedExactAgeCaseAssertionMatch.numericValue = undefined;

        if (livingDeceasedCaseAssertionMatch && !livingDeceasedCaseAssertionMatch.userOverride)
            livingDeceasedCaseAssertionMatch.assertionDescriptorValueGuid = undefined;

        //#endregion

        const endDateForCalcAgeLiving: string =
            CaseDataCustomLogicHelper.GetEndDateForAgeCalculation(
                currentCase.caseStatus,
                livingDeceasedCaseAssertionMatch,
                dateOfDeathCaseAssertionMatch,
                resolutionAgreementDateCaseAssertionMatch,
                dismissalDateCaseAssertionMatch
            );

        const validDOB =
            dobCaseAssertionMatch?.dateTimeValue &&
            Common.isValidDate(dobCaseAssertionMatch?.dateTimeValue.toString());
        const validDateOfDeath =
            dateOfDeathCaseAssertionMatch?.dateTimeValue &&
            Common.isValidDate(dateOfDeathCaseAssertionMatch?.dateTimeValue.toString());

        if (validDOB && endDateForCalcAgeLiving) {
            const calculatedAge = Common.dateDiffInYears(
                endDateForCalcAgeLiving,
                Common.safeDateFormat(dobCaseAssertionMatch?.dateTimeValue) ?? ''
            );

            CaseDataCustomLogicHelper.PopulateCalculatedExactAge(
                calculatedAge,
                assertionDescriptors,
                currentCase,
                calculatedExactAgeCaseAssertionMatch
            );

            CaseDataCustomLogicHelper.PopulateCalculatedAgeRange(
                calculatedAge,
                calculatedAgeRangeCaseAssertionMatch,
                estimatedAgeCaseAssertionMatch,
                assertionDescriptors,
                currentCase
            );
        }

        const livingDeceasedAD = assertionDescriptors.find(
            (x) => x.guid?.toUpperCase() === AssertionDescriptors.LivingDeceased.Guid
        );

        let livingDeceasedADV: IAssertionDescriptorModel | undefined = undefined;
        if (validDateOfDeath) {
            livingDeceasedADV = livingDeceasedAD?.assertionDescriptorValues?.find(
                (x) =>
                    x.guid!.toUpperCase() ===
                    AssertionDescriptors.LivingDeceased.DeceasedADVGuid.toUpperCase()
            );
        } else if (
            validDOB &&
            (!livingDeceasedCaseAssertionMatch ||
                !livingDeceasedCaseAssertionMatch.userOverride ||
                livingDeceasedCaseAssertionMatch.assertionDescriptorValueGuid?.toUpperCase() !==
                    AssertionDescriptors.LivingDeceased.DeceasedADVGuid.toUpperCase())
        ) {
            livingDeceasedADV = livingDeceasedAD?.assertionDescriptorValues?.find(
                (x) =>
                    x.guid!.toUpperCase() ===
                    AssertionDescriptors.LivingDeceased.LivingADVGuid.toUpperCase()
            );
        }

        CaseDataCustomLogicHelper.PopulateLivingDeceased(
            livingDeceasedADV,
            livingDeceasedCaseAssertionMatch,
            livingDeceasedAD,
            currentCase
        );
    }

    private static RunSmokingRelatedCustomLogic(
        currentCase: ICaseModel,
        assertionDescriptors: IAssertionDescriptorModel[]
    ) {
        //#region FIND ALL THE CASE ASSERTION MATCHES FROM THE CURRENT CASE
        const smokingHistoryPacksPerDayCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.SmokingHistoryPacksPerDay.Guid.toUpperCase()
        );
        const smokingHistoryPackYearCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.SmokingHistoryPackYear.Guid.toUpperCase()
        );
        const smokingStartDateCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.SmokingStartDate.Guid.toUpperCase()
        );
        const smokingEndDateCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.SmokingEndDate.Guid.toUpperCase()
        );
        const isSmokerCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.IsSmoker.Guid.toUpperCase()
        );

        //#endregion

        //#region  RESET ALL INFERRED VALUES TO NULL; THE LOGIC BELOW CAN RECALCULATE

        if (isSmokerCaseAssertionMatch && !isSmokerCaseAssertionMatch.userOverride) {
            isSmokerCaseAssertionMatch.assertionDescriptorValueGuid = undefined;
        }

        if (
            smokingHistoryPackYearCaseAssertionMatch &&
            !smokingHistoryPackYearCaseAssertionMatch.userOverride
        ) {
            smokingHistoryPackYearCaseAssertionMatch.numericValue = undefined;
        }

        //#endregion

        //#region isSmoker

        const packYearAsNumber = smokingHistoryPackYearCaseAssertionMatch?.numericValue ?? 0;

        const isSmoker = !!(
            (smokingStartDateCaseAssertionMatch?.dateTimeValue &&
                Common.isValidDate(smokingStartDateCaseAssertionMatch.dateTimeValue)) ||
            (smokingEndDateCaseAssertionMatch?.dateTimeValue &&
                Common.isValidDate(smokingEndDateCaseAssertionMatch.dateTimeValue)) ||
            smokingHistoryPacksPerDayCaseAssertionMatch?.assertionDescriptorValueGuid ||
            packYearAsNumber > 0
        );

        if (isSmoker) {
            const isSmokerAD = assertionDescriptors.find(
                (x) => x.guid?.toUpperCase() === AssertionDescriptors.IsSmoker.Guid
            );

            const isSmokerYesADV = isSmokerAD?.assertionDescriptorValues?.find(
                (x) =>
                    x.guid?.toUpperCase() === AssertionDescriptors.IsSmoker.YesADVGuid.toUpperCase()
            );

            CaseDataCustomLogicHelper.PopulateIsSmokerYes(
                isSmokerYesADV,
                isSmokerCaseAssertionMatch,
                isSmokerAD,
                currentCase
            );
        }

        //#endregion

        //#region PACK YEARS

        let packYears = 0;

        const hasUserSuppliedEndDate = Common.isValidDate(
            smokingEndDateCaseAssertionMatch?.dateTimeValue ?? ''
        );

        const smokingHistoryPacksPerDayAD = assertionDescriptors.find(
            (ad) =>
                ad.guid?.toUpperCase() ===
                AssertionDescriptors.SmokingHistoryPacksPerDay.Guid.toUpperCase()
        );

        const smokingHistoryPacksPerDayADV =
            smokingHistoryPacksPerDayAD?.assertionDescriptorValues?.find(
                (adv) =>
                    adv.guid?.toUpperCase() ===
                    smokingHistoryPacksPerDayCaseAssertionMatch?.assertionDescriptorValueGuid?.toUpperCase()
            );

        const packsPerDayNumericValue = smokingHistoryPacksPerDayADV?.numericValue ?? 0;

        if (packsPerDayNumericValue > 0) {
            let smokingDays = 0;

            const startDate =
                Common.safeDateFormat(smokingStartDateCaseAssertionMatch?.dateTimeValue) ?? '';
            const endDate =
                Common.safeDateFormat(smokingEndDateCaseAssertionMatch?.dateTimeValue) ?? '';

            const hasValidStartDate = Common.isValidDate(startDate);

            if (hasValidStartDate) {
                const smokingEndDate = hasUserSuppliedEndDate ? endDate : Common.currentDate();

                smokingDays = Common.dateDiffInDays(smokingEndDate, startDate);
            }

            if (smokingDays > 0) {
                packYears = packsPerDayNumericValue * (smokingDays / 365);
            }
        }

        CaseDataCustomLogicHelper.PopulateSmokingPackYears(
            packYears,
            smokingHistoryPackYearCaseAssertionMatch,
            assertionDescriptors,
            currentCase
        );

        //#endregion
    }

    private static RunDemandRelatedCustomLogic(
        currentCase: ICaseModel,
        assertionDescriptors: IAssertionDescriptorModel[]
    ) {
        //#region FIND ALL THE CASE ASSERTION MATCHES FROM THE CURRENT CASE

        const demandForCorporateRepDepoCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.DemandForCorporateRepDepo.Guid.toUpperCase()
        );
        const corporateRepDepoReceivedDateCaseAssertionMatch = currentCase.caseAssertions!.find(
            (x) =>
                x.assertionDescriptorGuid?.toUpperCase() ===
                AssertionDescriptors.CorporateRepDemandReceivedDate.Guid.toUpperCase()
        );

        //#endregion

        //#region  RESET ALL INFERRED VALUES TO NULL; THE LOGIC BELOW CAN RECALCULATE
        if (
            demandForCorporateRepDepoCaseAssertionMatch &&
            !demandForCorporateRepDepoCaseAssertionMatch.userOverride
        )
            demandForCorporateRepDepoCaseAssertionMatch.booleanValue = undefined;
        //#endregion

        if (
            corporateRepDepoReceivedDateCaseAssertionMatch &&
            corporateRepDepoReceivedDateCaseAssertionMatch.dateTimeValue &&
            Common.isValidDate(corporateRepDepoReceivedDateCaseAssertionMatch.dateTimeValue)
        ) {
            const demandForCorporateRepDepoAD = assertionDescriptors.find(
                (x) => x.guid!.toUpperCase() == AssertionDescriptors.DemandForCorporateRepDepo.Guid
            );

            // User added Corporate Rep Depo Received Date. Hence ALWAYS Default Demand for Corporate Rep Depo to TRUE
            CaseDataCustomLogicHelper.SetDemandforCorporateRepDepTrue(
                demandForCorporateRepDepoCaseAssertionMatch,
                demandForCorporateRepDepoAD,
                currentCase
            );
        }
    }

    private static SetDemandforCorporateRepDepTrue(
        demandForCorporateRepDepoCaseAssertionMatch: ICaseAssertionModel | undefined,
        demandForCorporateRepDepoAD: IAssertionDescriptorModel | undefined,
        currentCase: ICaseModel
    ) {
        if (demandForCorporateRepDepoCaseAssertionMatch) {
            demandForCorporateRepDepoCaseAssertionMatch.booleanValue = true;
            demandForCorporateRepDepoCaseAssertionMatch.userOverride = false;
        } else if (demandForCorporateRepDepoAD) {
            if (!currentCase.caseAssertions) currentCase.caseAssertions = [];
            currentCase.caseAssertions.push({
                assertionDescriptorGuid: demandForCorporateRepDepoAD.guid,
                booleanValue: true,
            });
        }
    }

    private static PopulateSmokingPackYears(
        packYears: number,
        smokingHistoryPackYearCaseAssertionMatch: ICaseAssertionModel | undefined,
        assertionDescriptors: IAssertionDescriptorModel[],
        currentCase: ICaseModel
    ) {
        const packYearsRounded = Math.round(packYears * 10) / 10;

        if (packYears) {
            if (smokingHistoryPackYearCaseAssertionMatch) {
                smokingHistoryPackYearCaseAssertionMatch.numericValue = packYearsRounded;
                smokingHistoryPackYearCaseAssertionMatch.userOverride = false;
            } else {
                const smokingHistoryPackYearAD = assertionDescriptors.find(
                    (ad) =>
                        ad.guid?.toUpperCase() ===
                        AssertionDescriptors.SmokingHistoryPackYear.Guid.toUpperCase()
                );
                if (smokingHistoryPackYearAD) {
                    if (!currentCase.caseAssertions) {
                        currentCase.caseAssertions = [];
                    }

                    currentCase.caseAssertions.push({
                        assertionDescriptorGuid: smokingHistoryPackYearAD.guid,
                        numericValue: packYearsRounded,
                        userOverride: true,
                    });
                }
            }
        } else {
            if (smokingHistoryPackYearCaseAssertionMatch) {
                smokingHistoryPackYearCaseAssertionMatch.numericValue = undefined;
                smokingHistoryPackYearCaseAssertionMatch.userOverride = false;
            }
        }
    }

    private static PopulateIsSmokerYes(
        isSmokerYesADV: IAssertionDescriptorValueModel | undefined,
        isSmokerCaseAssertionMatch: ICaseAssertionModel | undefined,
        isSmokerAD: IAssertionDescriptorModel | undefined,
        currentCase: ICaseModel
    ) {
        if (isSmokerYesADV) {
            if (isSmokerCaseAssertionMatch) {
                isSmokerCaseAssertionMatch.assertionDescriptorValueGuid = isSmokerYesADV.guid;
                isSmokerCaseAssertionMatch.userOverride = false;
            } else {
                if (isSmokerAD) {
                    if (!currentCase.caseAssertions) currentCase.caseAssertions = [];
                    currentCase.caseAssertions.push({
                        assertionDescriptorGuid: isSmokerAD.guid,
                        assertionDescriptorValueGuid: isSmokerYesADV.guid,
                        userOverride: false,
                    });
                }
            }
        }
    }

    private static PopulateCalculatedAgeRange(
        calculatedAge: number,
        calculatedAgeRangeCaseAssertionMatch: ICaseAssertionModel | undefined,
        estimatedAgeRangeCaseAssertionMatch: ICaseAssertionModel | undefined,
        assertionDescriptors: IAssertionDescriptorModel[],
        currentCase: ICaseModel
    ) {
        if (calculatedAge) {
            if (estimatedAgeRangeCaseAssertionMatch) {
                estimatedAgeRangeCaseAssertionMatch.assertionDescriptorValueGuid = undefined;
                estimatedAgeRangeCaseAssertionMatch.userOverride = false;
            }

            const calculatedAgeRangeAD = assertionDescriptors.find(
                (x) =>
                    x.guid?.toUpperCase() ===
                    AssertionDescriptors.CalculatedAgeRange.Guid.toUpperCase()
            );

            const match = CalculatedAgeAssertionDescriptorValues.find(
                (x) => calculatedAge >= x.From && calculatedAge <= x.To
            );

            const calculatedAgeRangeADV = calculatedAgeRangeAD?.assertionDescriptorValues?.find(
                (x) => x.guid?.toUpperCase() === match?.Guid.toUpperCase()
            );

            if (calculatedAgeRangeCaseAssertionMatch) {
                calculatedAgeRangeCaseAssertionMatch.assertionDescriptorValueGuid =
                    calculatedAgeRangeADV?.guid;
            } else if (calculatedAgeRangeAD && calculatedAgeRangeADV) {
                if (!currentCase.caseAssertions) currentCase.caseAssertions = [];
                currentCase.caseAssertions.push({
                    assertionDescriptorGuid: calculatedAgeRangeAD.guid,
                    assertionDescriptorValueGuid: calculatedAgeRangeADV.guid,
                });
            }
        }
    }

    private static PopulateLivingDeceased(
        deceasedADV: IAssertionDescriptorValueModel | undefined,
        livingDeceasedCaseAssertionMatch: ICaseAssertionModel | undefined,
        livingDeceasedAD: IAssertionDescriptorModel | undefined,
        currentCase: ICaseModel
    ) {
        if (deceasedADV) {
            if (livingDeceasedCaseAssertionMatch) {
                livingDeceasedCaseAssertionMatch.assertionDescriptorValueGuid = deceasedADV.guid;
                livingDeceasedCaseAssertionMatch.userOverride = false;
            } else {
                if (livingDeceasedAD) {
                    if (!currentCase.caseAssertions) currentCase.caseAssertions = [];
                    currentCase.caseAssertions.push({
                        assertionDescriptorGuid: livingDeceasedAD.guid,
                        assertionDescriptorValueGuid: deceasedADV.guid,
                    });
                }
            }
        }
    }

    private static GetEndDateForAgeCalculation(
        caseStatus: ICaseStatusModel | undefined,
        livingDeceasedCaseAssertionMatch: ICaseAssertionModel | undefined,
        dateOfDeathCaseAssertionMatch: ICaseAssertionModel | undefined,
        resolutionAgreementDateCaseAssertionMatch: ICaseAssertionModel | undefined,
        dismissalDateCaseAssertionMatch: ICaseAssertionModel | undefined
    ) {
        let endDateForCalcAgeLiving = '';

        if (Common.isValidDate(dateOfDeathCaseAssertionMatch?.dateTimeValue ?? '')) {
            endDateForCalcAgeLiving = dateOfDeathCaseAssertionMatch?.dateTimeValue ?? '';
        } else if (
            livingDeceasedCaseAssertionMatch?.assertionDescriptorValueGuid?.toUpperCase() ===
                AssertionDescriptors.LivingDeceased.DeceasedADVGuid.toUpperCase() &&
            !Common.isValidDate(dateOfDeathCaseAssertionMatch?.dateTimeValue || '')
        ) {
            // Cannot Calculate Age if User set to Deceased but didnt add a Date of Death
            endDateForCalcAgeLiving = '';
        }
        // If the case is a Resolved or Alt-Closed case, only User is Living, Only Calculate Age with End Date = Resolution Date. Not current Date.
        else if (
            caseStatus &&
            (caseStatus.status?.id === CaseStatusTypesEnum.Resolved.Value ||
                (caseStatus.status?.id === CaseStatusTypesEnum.Alt.Value &&
                    caseStatus.subStatus?.id === CaseSubStatusTypesEnum.Closed.Value))
        ) {
            if (
                resolutionAgreementDateCaseAssertionMatch &&
                resolutionAgreementDateCaseAssertionMatch.dateTimeValue &&
                Common.isValidDate(resolutionAgreementDateCaseAssertionMatch.dateTimeValue)
            )
                endDateForCalcAgeLiving = Common.dateFormat(
                    resolutionAgreementDateCaseAssertionMatch.dateTimeValue
                );
            else if (
                dismissalDateCaseAssertionMatch &&
                dismissalDateCaseAssertionMatch.dateTimeValue &&
                Common.isValidDate(dismissalDateCaseAssertionMatch.dateTimeValue)
            )
                endDateForCalcAgeLiving = Common.dateFormat(
                    dismissalDateCaseAssertionMatch.dateTimeValue
                );
            else endDateForCalcAgeLiving = Common.currentDate();
        } else endDateForCalcAgeLiving = Common.currentDate();

        return endDateForCalcAgeLiving;
    }

    private static PopulateCalculatedExactAge(
        calculatedAge: number,
        assertionDescriptors: IAssertionDescriptorModel[],
        currentCase: ICaseModel,
        calculatedExactAgeCaseAssertionMatch?: ICaseAssertionModel
    ) {
        if (calculatedExactAgeCaseAssertionMatch) {
            calculatedExactAgeCaseAssertionMatch.numericValue = calculatedAge;
        } else {
            const calculatedExactAgeAD = assertionDescriptors.find(
                (x) => x.guid?.toUpperCase() === AssertionDescriptors.CalculatedExactAge.Guid
            );
            if (calculatedExactAgeAD) {
                if (!currentCase.caseAssertions) currentCase.caseAssertions = [];
                currentCase.caseAssertions.push({
                    assertionDescriptorGuid: calculatedExactAgeAD.guid,
                    numericValue: calculatedAge,
                });
            }
        }
    }
}
